import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import api from '../api';

export default function InterviewUploadPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0); // 0 = file, 1 = paste
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [rawText, setRawText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const fileInputRef = useRef(null);

  const ACCEPTED = '.docx,.md,.txt';

  function handleFile(f) {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['docx', 'md', 'txt'].includes(ext)) {
      setError('Only .docx, .md, or .txt files are supported');
      return;
    }
    setError('');
    setFile(f);
  }

  function onDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function onDragLeave() {
    setDragging(false);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  }

  async function handleExtract() {
    setError('');
    setLoading(true);
    setProgress('Sending to AI for analysis…');

    try {
      let res;

      if (tab === 0 && file) {
        setProgress('Reading your document…');
        const formData = new FormData();
        formData.append('file', file);
        res = await api.post('/api/interview/extract', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (tab === 1 && rawText.trim()) {
        setProgress('Analysing pasted text…');
        const formData = new FormData();
        formData.append('raw_text', rawText.trim());
        res = await api.post('/api/interview/extract', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        setError(tab === 0 ? 'Please select a file first' : 'Please paste some text first');
        setLoading(false);
        return;
      }

      const { stories, source_filename, chunks_processed } = res.data;

      if (!stories || stories.length === 0) {
        setError('No interview stories were detected in this content. Try selecting a different section or adding more detail.');
        setLoading(false);
        return;
      }

      navigate('/interview/review', {
        state: { stories, sourceFilename: source_filename, chunksProcessed: chunks_processed },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Extraction failed. Please try again.');
    } finally {
      setLoading(false);
      setProgress('');
    }
  }

  const canExtract = tab === 0 ? !!file : rawText.trim().length > 50;

  return (
    <Box sx={{ bgcolor: '#0d1117', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--tx-hi)', mb: 0.5 }}>
            Upload & Extract Stories
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--tx-dim)' }}>
            Upload a document or paste text — AI will detect and extract your interview stories for review.
          </Typography>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setError(''); setFile(null); }}
          sx={{
            mb: 3,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            '& .MuiTab-root': { color: 'var(--tx-dim)', textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: '#818cf8' },
            '& .MuiTabs-indicator': { bgcolor: '#6366f1' },
          }}
        >
          <Tab icon={<UploadFileIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Upload File" />
          <Tab icon={<TextSnippetOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Paste Text" />
        </Tabs>

        {/* File upload */}
        {tab === 0 && (
          <Box
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragging ? '#6366f1' : file ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 3,
              p: 6,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragging ? 'rgba(99,102,241,0.06)' : file ? 'rgba(99,102,241,0.04)' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'rgba(99,102,241,0.5)',
                bgcolor: 'rgba(99,102,241,0.04)',
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />
            <UploadFileIcon sx={{ fontSize: 48, color: file ? '#6366f1' : 'var(--tx-gh)', mb: 2 }} />
            {file ? (
              <>
                <Typography variant="h6" sx={{ color: 'var(--tx-hi)', fontWeight: 700, mb: 0.5 }}>
                  {file.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--tx-dim)' }}>
                  {(file.size / 1024).toFixed(1)} KB · Click to change
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ color: 'var(--tx-lo)', fontWeight: 600, mb: 0.5 }}>
                  Drop a file here or click to browse
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--tx-ft)' }}>
                  Supports .docx, .md, .txt
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Paste text */}
        {tab === 1 && (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={14}
              placeholder={`Paste your resume, cover letter, or career notes here…\n\nThe AI will scan through and identify distinct interview stories — situations where you made an impact, solved a problem, led a team, or drove a result.`}
              value={rawText}
              onChange={e => setRawText(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.02)',
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                },
              }}
            />
            {rawText.length > 0 && (
              <Typography variant="caption" sx={{ color: 'var(--tx-ft)', mt: 0.5, display: 'block' }}>
                {rawText.length.toLocaleString()} characters
                {rawText.length > 6000 && ' · Will be processed in multiple batches'}
              </Typography>
            )}
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Progress */}
        {loading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5, '& .MuiLinearProgress-bar': { bgcolor: '#6366f1' } }} />
            <Typography variant="body2" sx={{ color: 'var(--tx-dim)', textAlign: 'center' }}>
              {progress || 'Analysing with AI…'}
            </Typography>
          </Box>
        )}

        {/* Action */}
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3, gap: 1.5 }}>
          <Button
            onClick={() => navigate('/interview')}
            sx={{ color: 'var(--tx-dim)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleExtract}
            disabled={loading || !canExtract}
            sx={{
              bgcolor: '#6366f1',
              '&:hover': { bgcolor: '#4f46e5' },
              '&.Mui-disabled': { bgcolor: 'rgba(99,102,241,0.3)', color: 'rgba(255,255,255,0.4)' },
            }}
          >
            {loading ? 'Extracting…' : 'Extract Stories'}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
